import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { CardComp, ButtonComp, ModalCompAdvance } from "../../../components";
import CustomSelect from "../../../components/custom-select/custom-select";
import { customStyles } from "../../../components/custom-select/custom-styles";
import { useGetBanksQuery } from "../../../store/selectableSlice";
import {
  useGetMyBankAccountQuery,
  useAddMyBankAccountMutation,
  useValidateMyBankAccountMutation,
} from "../../../store/member/member-bank-account";
import { Input } from "../../../components/Ui/new-input";
import Loader from "../../../components/Ui/Loader";

const BankAccountTab = () => {
  const [showModal, setShowModal] = useState(false);
  const [validatedAccountName, setValidatedAccountName] = useState("");
  const [validationError, setValidationError] = useState("");

  const {
    data: bankAccountData,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetMyBankAccountQuery();

  // Determine if we have a valid bank account
  const hasValidBankAccount = (account) => {
    if (!account) return false;
    if (account === null) return false;
    if (typeof account !== "object") return false;
    // Check if it's an empty object
    if (Object.keys(account).length === 0) return false;
    return !!(account.id || account.account_number);
  };

  // Final bank account value - null if no valid data
  // Backend returns null when no bank account found, so we check for valid data
  const bankAccount = hasValidBankAccount(bankAccountData)
    ? bankAccountData
    : null;

  const { data: banks = [], isLoading: isLoadingBanks } = useGetBanksQuery();
  const [addBankAccount, { isLoading: isAdding }] =
    useAddMyBankAccountMutation();
  const [validateAccount, { isLoading: isValidating }] =
    useValidateMyBankAccountMutation();

  const formik = useFormik({
    initialValues: {
      bank_id: "",
      account_number: "",
      account_name: "",
    },
    validationSchema: Yup.object({
      bank_id: Yup.string().required("Bank is required"),
      account_number: Yup.string()
        .required("Account number is required")
        .length(10, "Account number must be exactly 10 digits")
        .matches(/^\d+$/, "Account number must contain only digits"),
      account_name: Yup.string().required("Account name is required"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          bank_id: parseInt(values.bank_id),
          account_number: values.account_number,
          account_name: values.account_name,
        };

        await addBankAccount(payload).unwrap();
        toast.success("Bank account added successfully");
        setShowModal(false);
        formik.resetForm();
        setValidatedAccountName("");
        refetch();
      } catch (error) {
        toast.error(error?.data?.message || "Failed to save bank account");
      }
    },
  });

  useEffect(() => {
    if (!showModal) {
      formik.resetForm();
      setValidatedAccountName("");
      setValidationError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const handleValidateAccount = React.useCallback(async () => {
    if (!formik.values.bank_id || !formik.values.account_number) {
      setValidationError("");
      return;
    }

    if (formik.values.account_number.length !== 10) {
      setValidationError("");
      return;
    }

    try {
      const selectedBank = banks.find(
        (bank) => bank.id === parseInt(formik.values.bank_id)
      );
      if (!selectedBank) {
        setValidationError("Please select a bank");
        return;
      }

      // Clear previous errors
      setValidationError("");

      const response = await validateAccount({
        account_number: formik.values.account_number,
        bank_code: selectedBank.code,
      }).unwrap();

      if (response?.account_name) {
        setValidatedAccountName(response.account_name);
        formik.setFieldValue("account_name", response.account_name);
        setValidationError("");
        toast.success("Account validated successfully");
      } else {
        setValidatedAccountName("");
        formik.setFieldValue("account_name", "");
        setValidationError(
          "Could not validate account. Please check the account number and bank."
        );
      }
    } catch (error) {
      setValidatedAccountName("");
      formik.setFieldValue("account_name", "");
      const errorMessage =
        error?.data?.message ||
        "Failed to validate account. Please check the account number and bank.";
      setValidationError(errorMessage);
      toast.error(errorMessage);
    }
  }, [
    formik.values.bank_id,
    formik.values.account_number,
    banks,
    validateAccount,
  ]);

  useEffect(() => {
    if (formik.values.account_number?.length === 10 && formik.values.bank_id) {
      handleValidateAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.account_number,
    formik.values.bank_id,
    handleValidateAccount,
  ]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h5 className="fw-bold text-dark mb-0">Bank Account Information</h5>
        {!isLoading && !bankAccount && (
          <ButtonComp
            btnText="Add Bank Account"
            onClick={() => setShowModal(true)}
            btnClassName="bg-primary1 text-white border-0 rounded px-4 py-2 fw-semibold hover:opacity-90 transition-opacity"
            ButtonInlineStyle={{ minWidth: "fit-content" }}
          />
        )}
      </div>

      {bankAccount ? (
        <CardComp cardClassName="border-0 shadow-sm">
          <div className="p-4 space-y-4">
            <div>
              <p className="text-muted text-sm mb-1">Bank Name</p>
              <p className="fw-semibold text-dark">
                {bankAccount.bank_name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-muted text-sm mb-1">Account Number</p>
              <p className="fw-semibold text-dark">
                {bankAccount.account_number || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-muted text-sm mb-1">Account Name</p>
              <p className="fw-semibold text-dark">
                {bankAccount.account_name || "N/A"}
              </p>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800 mb-0">
                <strong>Note:</strong> You can only add your bank account once.
                To make changes, please contact your administrator.
              </p>
            </div>
          </div>
        </CardComp>
      ) : !isLoading ? (
        <CardComp cardClassName="border-0 shadow-sm">
          <div className="p-4 text-center">
            <p className="text-muted mb-0">
              No bank account information available
            </p>
          </div>
        </CardComp>
      ) : null}

      <ModalCompAdvance
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          formik.resetForm();
          setValidatedAccountName("");
          setValidationError("");
        }}
        size="md"
        centered
        modalTitle=""
        close={true}
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div>
                <h4 className="mb-0 text-xl py-3 text-black1 fw-bold text-center">
                  Add Bank Account
                </h4>
                <p className="text-sm text-gray-600 mb-4 text-center">
                  Enter your bank account details. We'll verify the account
                  number automatically.
                </p>
                <form className="w-full" onSubmit={formik.handleSubmit}>
                  <div className="space-y-4">
                    {/* Bank Selection */}
                    <div>
                      <CustomSelect
                        options={banks.map((bank) => ({
                          value: bank.id.toString(),
                          label: bank.title || bank.name,
                        }))}
                        value={formik.values.bank_id || null}
                        styles={customStyles}
                        label="Bank"
                        isLoading={isLoadingBanks}
                        onChange={(value) => {
                          formik.setFieldValue("bank_id", value.value);
                          setValidatedAccountName("");
                          setValidationError("");
                        }}
                        placeholder="Select your bank"
                      />
                      {formik.touched.bank_id && formik.errors.bank_id && (
                        <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                          {formik.errors.bank_id}
                        </span>
                      )}
                    </div>

                    {/* Account Number */}
                    <div>
                      <Input
                        type="text"
                        label="Account Number"
                        placeholder="Enter 10-digit account number"
                        name="account_number"
                        onChange={(e) => {
                          formik.handleChange(e);
                          setValidationError("");
                          setValidatedAccountName("");
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.account_number}
                        error={
                          formik.touched.account_number &&
                          formik.errors.account_number
                        }
                        errorMessage={formik.errors.account_number}
                        maxLength={10}
                      />
                    </div>

                    {/* Account Name */}
                    <div>
                      <Input
                        label="Account Name"
                        placeholder={
                          isValidating
                            ? "Validating account..."
                            : "Account name will appear after validation"
                        }
                        name="account_name"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.account_name}
                        error={
                          formik.touched.account_name &&
                          formik.errors.account_name
                        }
                        errorMessage={formik.errors.account_name}
                        readOnly
                        loading={isValidating}
                      />
                    </div>

                    {/* Validation Success Message */}
                    {validatedAccountName && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <p className="text-sm text-green-700 mb-0">
                          ✓ Account validated:{" "}
                          <strong>{validatedAccountName}</strong>
                        </p>
                      </div>
                    )}

                    {/* Validation Error Message */}
                    {validationError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-sm text-red-700 mb-0">
                          {validationError}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="my-4 w-full flex justify-center">
                    <button
                      type="submit"
                      className="w-[200px] h-[50px] rounded-[10px] bg-new-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        !validatedAccountName || isValidating || isAdding
                      }
                    >
                      {isAdding ? "Saving..." : "Save Bank Account"}
                    </button>
                  </div>
                </form>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default BankAccountTab;
